import React, { useState } from "react";
import { Box, Typography, Container, useTheme } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useMembership } from "../../contexts/MembershipContext";
import {
  AccountBalance as PortfolioIcon,
  MonetizationOn as DividendsIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import CurrentPortfolio from "../Dashboard/Charts/components/CurrentPortfolio";
import DividendsView from "../Dashboard/DashboardTabs/DividendsView";
import DividendCalendarView from "../Dashboard/DashboardTabs/DividendCalendarView";
import { useCurrentPortfolio } from "../Dashboard/Charts/hooks/useCurrentPortfolio";
import {
  getFlatPortfolioColors,
  portfolioTypography,
} from "../../utils/getFlatPortfolioColors";

export type PortfolioTab = "portfolio" | "dividends" | "dividend-calendar";

interface TabConfig {
  id: PortfolioTab;
  label: string;
  icon: React.ReactNode;
  component: React.ReactNode;
}

const PortfolioPage: React.FC = () => {
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);
  const [activeTab, setActiveTab] = useState<PortfolioTab>("portfolio");
  const { isRefetching } = useCurrentPortfolio();
  const { isMembershipActive } = useMembership();

  if (!isMembershipActive) {
    return <Navigate to="/dashboard" replace />;
  }

  const tabs: TabConfig[] = [
    {
      id: "portfolio",
      label: "Portfolio",
      icon: <PortfolioIcon />,
      component: <CurrentPortfolio />,
    },
    {
      id: "dividends",
      label: "Dividends",
      icon: <DividendsIcon />,
      component: <DividendsView />,
    },
    {
      id: "dividend-calendar",
      label: "Dividend Calendar",
      icon: <CalendarIcon />,
      component: <DividendCalendarView />,
    },
  ];

  const activeTabConfig = tabs.find((tab) => tab.id === activeTab);

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      {/* Tab Navigation - Flat UI segmented control */}
      <Box
        data-tour="portfolio-tabs"
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 4,
          borderBottom: `1px solid ${colors.border.primary}`,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            backgroundColor: "transparent", // Seamless blend with background
            borderRadius: 0, // Sharp corners - no rounding
            padding: 0,
            // NO shadows, NO blur, NO gradients - pure flat design
          }}
        >
          {tabs.map((tab, index) => (
            <Box
              key={tab.id}
              data-tour={`portfolio-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                minWidth: { xs: 100, sm: 120 },
                cursor: "pointer",
                transition:
                  "background-color 0.2s linear, border-color 0.2s linear",
                borderRadius: 0, // Sharp corners
                borderBottom:
                  activeTab === tab.id
                    ? `2px solid ${colors.functional.success}`
                    : "2px solid transparent",
                backgroundColor: "transparent", // Always transparent for seamless blend
                "&:hover": {
                  backgroundColor:
                    activeTab !== tab.id
                      ? colors.background.hover
                      : "transparent",
                },
                // NO gradients, NO shadows, NO transforms - pure flat design
              }}
            >
              <Box
                sx={{
                  fontSize: 20,
                  color:
                    activeTab === tab.id
                      ? colors.functional.success
                      : colors.text.secondary,
                  transition: "color 0.2s linear",
                  "& svg": {
                    fontSize: "inherit",
                  },
                }}
              >
                {tab.icon}
              </Box>
              <Typography
                sx={{
                  ...portfolioTypography.label,
                  color:
                    activeTab === tab.id
                      ? colors.functional.success
                      : colors.text.secondary,
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  transition: "color 0.2s linear",
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Content Area */}
      <Box
        data-tour="portfolio-content"
        sx={{
          position: "relative",
          minHeight: "60vh",
        }}
      >
        <Box
          sx={{
            opacity: 1,
            transform: "translateY(0)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {activeTabConfig?.component}
        </Box>
      </Box>
    </Container>
  );
};

export default PortfolioPage;
